import { isEmpty } from "lodash";
import type { RootState } from "store/types";

// `profile` (src/reducers/profile.js) unwound at the selector boundary for containers/Games
// (PLAY-5160), following store/selectors/filters.ts's shape.
export function selectShowCashier(state: RootState): boolean | undefined {
  try {
    return state.profile.get("showCashier");
  } catch {
    return undefined;
  }
}

export function selectShowSwitchDevice(state: RootState): boolean | undefined {
  try {
    return state.profile.get("showSwitchDevice");
  } catch {
    return undefined;
  }
}

// mapStateToProps passed the found route object itself as `profileGameRoundHistoryLink`,
// but its only consumer (`CommonGameController`'s `showGameHistory` prop) only ever checks
// it for truthiness (`component/common/GameController/index.js`) — so this unwinds cleanly
// to a boolean rather than keeping the route Immutable and opaque.
export function selectHasProfileGameRoundHistoryLink(state: RootState): boolean {
  try {
    return !!state.profile.get("links").find((link: any) => link.get("rel") === "games-round-history.links");
  } catch {
    return false;
  }
}

// mapStateToProps passed the found link itself as `betHistoryLink`, but its only consumer
// (`containers/BetHistory`'s render guard, `!this.props.betHistoryLink`) only ever checked it
// for truthiness — its one other reference, the `fetchBetHistory` mergeProps wrapper, is dead
// (PLAY-5216, Phase 1 design.md D4) — so this unwinds to a boolean, same as
// `selectHasProfileGameRoundHistoryLink` above.
export function selectHasBetHistoryLink(state: RootState): boolean {
  try {
    return !!state.profile.get("links").find((link: any) => link.get("rel") === "service.url");
  } catch {
    return false;
  }
}

// containers/GameNavigation (PLAY-5216, Phase 1): both `exitLink`/`continuePlayingLink` fed
// only into the combined `showMenu` OR-expression in mapStateToProps, same truthiness-only
// shape as `selectHasBetHistoryLink`/`selectHasProfileGameRoundHistoryLink` above (not
// forwarded anywhere else in the component) — unwound to booleans, not the opaque link finder
// design.md D5 originally proposed.
export function selectHasExitLink(state: RootState): boolean {
  try {
    return !!state.profile.get("links").find((link: any) => link.get("rel") === "exit");
  } catch {
    return false;
  }
}

export function selectHasContinuePlayingLink(state: RootState): boolean {
  try {
    return !!state.profile.get("links").find((link: any) => link.get("rel") === "categories-home.recentlyplayed");
  } catch {
    return false;
  }
}

// containers/Navigation (PLAY-5216, Phase 3): both fed only into the combined `showMenu`
// OR-expression in mapStateToProps, same truthiness-only shape as the selectors above.
export function selectHasRewardsLink(state: RootState): boolean {
  try {
    return !!state.profile.get("links").find((link: any) => link.get("rel") === "rewards");
  } catch {
    return false;
  }
}

export function selectHasTournamentsLink(state: RootState): boolean {
  try {
    return !!state.profile.get("links").find((link: any) => link.get("rel") === "tournaments");
  } catch {
    return false;
  }
}

export function selectCanToggleProviderLogoOnGames(state: RootState): boolean | undefined {
  try {
    return state.profile.getIn(["uiControls", "canToggleProviderLogoOnGames"]);
  } catch {
    return undefined;
  }
}

// containers/Error (PLAY-5216, Phase 2): unlike selectHasExitLink above, the found link here is
// passed to the still-unconverted `fetchExit` thunk (`this.props.fetchExit(this.props.exitLink)`),
// not just checked for truthiness — stays Immutable and opaque, same convention as
// historyLinks.ts's selectHistoryLink.
export function selectExitLink(state: RootState): any {
  try {
    return state.profile.get("links").find((link: any) => link.get("rel") === "exit");
  } catch {
    return undefined;
  }
}

// containers/History (PLAY-5216, Phase 3): same opaque-passthrough convention as selectExitLink
// above - the found link is passed straight into ActionCreator.fetchGameRoundHistoryLinks
// (`route.get("href")`/`route.get("method")`), not just checked for truthiness.
export function selectHistoryLinksLink(state: RootState): any {
  try {
    return state.profile.get("links").find((link: any) => link.get("rel") === "history.links");
  } catch {
    return undefined;
  }
}

// containers/History (PLAY-5216, Phase 3) - distinct from user.ts's selectBalance, a different
// slice.
export function selectProfileBalance(state: RootState): any {
  return state.profile.get("balance");
}

export function selectProfileCurrency(state: RootState): string | undefined {
  return state.profile.get("currency") || undefined;
}

export function selectProfileSymbol(state: RootState): string | undefined {
  const symbolList = state.profile.get("symbol");
  if (isEmpty(symbolList)) {
    return undefined;
  }
  return symbolList.map((symbol: number) => String.fromCharCode(symbol)).toJSON().join("");
}
